import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Phone, Trophy, Broadcast, Lightning } from "@phosphor-icons/react";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", mobile: "", email: "", password: "", organization: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await register(form);
    setBusy(false);
    if (res.ok) nav("/dashboard", { replace: true });
    else setError(res.error);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" style={{ fontFamily: "Inter, sans-serif" }} data-testid="register-page">
      {/* LEFT hero */}
      <div className="relative bg-blue-500 text-white overflow-hidden hidden lg:flex flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 30%, white 0%, transparent 30%), radial-gradient(circle at 80% 60%, white 0%, transparent 20%)" }} />
        <Link to="/" className="relative z-10 flex items-center gap-3">
          <img src="/nitisoft-logo.png" alt="NitiSoft" className="h-12 bg-white rounded p-1" />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest">NitiSoft</div>
            <div className="text-[10px] uppercase tracking-widest opacity-80">Player Auction</div>
          </div>
        </Link>

        <div className="relative z-10">
          <div className="text-xs uppercase tracking-widest font-bold text-yellow-300 mb-3">ORGANIZE YOUR OWN TOURNAMENT</div>
          <h1 className="font-black uppercase leading-[1.05]" style={{ fontSize: "clamp(40px, 5vw, 68px)" }}>
            Host your<br />auction night.
          </h1>
          <p className="mt-4 text-white/95 max-w-md text-lg">
            Create tournament auctions, invite team owners, run real-time bidding with LED &amp; stream overlays — everything under your own admin account.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
            {[
              [Lightning, "Free", "to start"],
              [Broadcast, "Realtime", "bidding"],
              [Trophy, "Broadcast", "OBS overlays"],
            ].map(([Icon, l1, l2]) => (
              <div key={l1} className="bg-white/10 backdrop-blur rounded-lg px-3 py-3 text-center border border-white/20">
                <Icon size={22} className="text-yellow-300 mx-auto mb-1" weight="fill" />
                <div className="font-black text-base leading-tight">{l1}</div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">{l2}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/70 flex items-center gap-2">
          <Phone size={12} weight="fill" /> +91-9970228557 · nitisoft.in
        </div>
      </div>

      {/* RIGHT form */}
      <div className="flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/nitisoft-logo.png" alt="NitiSoft" className="h-10" />
            <span className="font-bold text-blue-600 uppercase tracking-widest text-sm">NitiSoft Auction</span>
          </Link>

          <div className="text-xs font-bold uppercase tracking-widest text-red-500 mb-2">CREATE ACCOUNT</div>
          <h2 className="font-black text-4xl sm:text-5xl mb-2 uppercase leading-tight text-gray-900" data-testid="register-heading">
            Become an Organizer.
          </h2>
          <p className="text-gray-600 mb-8">Register as a tournament admin. You can create multiple auctions.</p>

          <form onSubmit={submit} data-testid="register-form" className="space-y-4">
            {[
              { key: "full_name", label: "Full Name*", type: "text", placeholder: "Your full name", required: true },
              { key: "mobile", label: "Mobile*", type: "tel", placeholder: "+91 xxxxx xxxxx", required: true },
              { key: "email", label: "Email*", type: "email", placeholder: "you@example.com", required: true },
              { key: "password", label: "Password*", type: "password", placeholder: "min. 6 characters", required: true },
              { key: "organization", label: "Organization / Tournament Group", type: "text", placeholder: "Optional — e.g. Mumbai Corporate League", required: false },
            ].map(({ key, label, type, placeholder, required }) => (
              <div key={key}>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-600 block mb-2">{label}</label>
                <input
                  data-testid={`register-${key}-input`}
                  type={type}
                  required={required}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={placeholder}
                  className="w-full bg-gray-50 border-2 border-gray-200 focus:border-blue-500 focus:bg-white outline-none rounded-lg px-4 py-3 text-gray-900 placeholder:text-gray-400"
                />
              </div>
            ))}

            {error && <div data-testid="register-error" className="text-red-700 text-sm border-2 border-red-200 bg-red-50 rounded-lg px-3 py-2 font-semibold">{error}</div>}

            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit-button"
              className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold uppercase tracking-widest px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              {busy ? "Creating…" : "Create Account"}
            </button>

            <div className="text-sm text-gray-600 pt-2 flex justify-between font-semibold">
              <Link to="/" className="hover:text-blue-600">← Home</Link>
              <Link to="/login" className="hover:text-blue-600" data-testid="link-to-login">Sign in instead →</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
